/**
Project: Voice Panel (c)
Title: Domains
Description: Component to verify the domains.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import {
  Text,
  Flex,
  Popup,
  Input,
  Button,
  Tooltip,
  ErrorIcon,
  AcceptIcon,
  CallVideoIcon,
  ClipboardCopiedToIcon,
} from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import ResponseUtil from "../../../../core/helpers/response-util";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { manageError } from "../../../../core/actions/common-actions";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  setDomainSetupProgressPage,
  getDomainRecords,
  setUpdatedDomainRecords,
  saveDomainRecords,
} from "../actions/domain-setup-action";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import PageLoader from "../../common/helpers/page-loader";
import PageExitGuard from "../../common/helpers/page-exit-guard";

class Domains extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_domainsetup = "vpa_domainsetup";
  constructor(props) {
    super(props);
    this.state = {
      copieClipboard: false,
      isFormDataFilled: false,
      errorMessage: null,
      errorDetails: null,
      isError: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.checkAccountNumber();
    await this.loadPageData();
    await this.checkDomainFields();
    if (this.props.domainDnsRecord.length === 0) {
      await this.props.setDomainSetupProgressPage(
        VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.REGION
      );
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  checkAccountNumber = async () => {
    if (
      this.props.domainSetupProgressAccountNumber === null ||
      this.props.domainSetupProgressAccountNumber.trim() === ""
    ) {
      await this.props.setDomainSetupProgressPage(
        VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.WELCOME
      );
    }
  };

  loadPageData = async () => {
    try {
      await this.setStateAsync({ isPageDataFetched: false });
      await this.props.getDomainRecords(
        this._cancelToken
      );
      await this.setStateAsync({ isPageDataFetched: true });
    } catch (err) {
      await this.setState({ isError: true });
      await manageError(err, this.props.history);
    }
  };

  loadNextPage = async () => {
    await this.props.setDomainSetupProgressPage(
      VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.USERS
    );
  };

  checkDomainFields = async () => {
    if (this.props.domainDnsRecord) {
      let isDataFilled = this.props.domainDnsRecord.every((domainDnsRecord) => {
        if (domainDnsRecord.textRecord.trim() !== "") {
          return true;
        } else {
          return false;
        }
      });
      this.setStateAsync({ isFormDataFilled: isDataFilled });
    }
  };

  checkDomainStatus = async (domain) => {
    if (
      domain.domainSetupStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.SUCCESS
    ) {
      this.loadNextPage();
    }
    if (
      domain.domainProcessStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.IN_PROGRESS &&
      domain.domainSetupStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.FAILURE
    ) {
      if (domain.domainSetupError === null) {
        this.setStateAsync({
          errorMessage: IntlUtil.getText(
            this._intl_ns_vpa_domainsetup,
            "notification.error.txtRecordFailed"
          ),
        });
      } else if (domain.domainSetupError != null) {
        this.setStateAsync({
          errorMessage: domain.domainSetupError,
        });
      }
    }
  };

  handleFormFieldChange = async (event) => {
    const domainName = event.target.name;
    const textRecord = event.target.value;
    await this.props.setUpdatedDomainRecords(domainName, textRecord);
    await this.checkDomainFields();
  };

  handleRecordsSubmit = async () => {
    if (
      this.props.domainDnsRecord &&
      this.props.domainDnsRecord.length > 0
    ) {
      this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_domainsetup,
          "notification.info.pleaseWaitUpdatingData"
        )
      );
      const domainRecords = {
        domains: this.props.domainDnsRecord
      };
      await saveDomainRecords(domainRecords, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            await this.props.getDomainRecords(
              this._cancelToken
            );
            res.data.records.map((domain) => {
              return this.checkDomainStatus(domain);
            });
            this.props.hidePageWaitDialog();
          } else {
            this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "notification.error.domainsVerifyFailed"
              ),
            });
            this.props.hidePageWaitDialog();
          }
        })
        .catch(async (err) => {
          this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (
              primeError &&
              primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.DOMAINS_CREATE_FAILED
            ) {
              await this.setStateAsync({
                errorDetails: primeError.message,
              });
            }
            this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "notification.error.domainsVerifyFailed"
              ),
            });
            await this.props.getDomainRegions(
              this._cancelToken
            );
          } else {
            await this.setState({ isError: true });
            await manageError(err, this.props.history);
          }
        });
    }
  };

  getRecordstatus(domain) {
    if (
      domain.domainProcessStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.IN_PROGRESS &&
      domain.domainSetupStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.FAILURE
    ) {
      if (domain.domainSetupError != null) {
        return (
          <Popup
            pointing
            on="hover"
            trigger={<ErrorIcon className="domain-setup-inactive" />}
            content={
              <Text className="page-text">{domain.domainSetupError}</Text>
            }
            align="center"
          />
        );
      } else if (
        domain.domainSetupStatus ===
        VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.FAILURE &&
        domain.domainSetupError === null
      ) {
        return (
          <Popup
            pointing
            on="hover"
            trigger={<ErrorIcon className="domain-setup-inactive" />}
            content={
              <Text className="page-text">
                {IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "notification.error.txtRecordFailed"
                )}
              </Text>
            }
            align="center"
          />
        );
      }
    }
    if (
      domain.domainProcessStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.COMPLETED &&
      domain.domainSetupStatus ===
      VoiceAdminConstants.DOMAIN_SETUP.PROCESS_STATUS.SUCCESS
    ) {
      return (
        <Button
          className="domain-setup-active"
          text
          iconOnly
          icon={<AcceptIcon />}
        />
      );
    }
  }

  getAlertMessagesStatus() {
    if (this.state.errorMessage !== null) {
      return (
        <Row>
          <Col xs={12} className="m-b-20">
            <AlertErrorMessage
              content={IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "notification.error.domainsVerifyFailed"
              )}
            />
          </Col>
        </Row>
      );
    }
  }

  handleBackRegions = async () => {
    await this.props.setDomainSetupProgressPage(
      VoiceAdminConstants.DOMAIN_SETUP.PROGRESS.REGION
    );
  };

  render() {
    return (
      <Grid fluid className="page-grid m-t-15">
        <Row>
          <Col xs={12} className="m-b-15">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_domainsetup,
                "content.verifyDomains.domainTitle"
              )}
            </Text>
          </Col>
        </Row>
        <PageLoader
          status={this.state.isPageDataFetched}
          size="medium"
          labelPosition="below"
          label={IntlUtil.getText(
            this._intl_ns_common,
            "content.loadingInprogress"
          )}
          type="full-center"
        >
          {this.getAlertMessagesStatus()}
          <Row>
            <Col xs={12} sm={12} md={12} lg={5}>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <Flex>
                    <Text className="page-text">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_domainsetup,
                        "content.verifyDomains.domainText"
                      )}
                    </Text>
                  </Flex>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep1"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep2"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep3"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep4"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep5"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep6"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep7"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10">
                <Col xs={12}>
                  <span dangerouslySetInnerHTML={{
                    __html: IntlUtil.getText(
                      this._intl_ns_vpa_domainsetup,
                      "content.verifyDomains.domainTextStep8"
                    ).trim()
                  }} className="page-text">
                  </span>
                </Col>
              </Row>
              <Row className="m-t-10 m-b-10 m-m-b-10">
                <Col xs={12}>
                  <Flex>
                    <a
                      className="page-text text-fc-primary text-semibold"
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://www.microsoft.com/en-us/videoplayer/embed/RE4dN8c?pid=ocpVideo0-innerdiv-oneplayer&postJsllMsg=true&maskLevel=20&market=en-us"
                    >
                      <CallVideoIcon className="m-r-5 p-b-5 p-t-5" />
                      {IntlUtil.getText(
                        this._intl_ns_vpa_domainsetup,
                        "content.verifyDomains.domainVideo"
                      )}
                    </a>
                  </Flex>
                </Col>
              </Row>
            </Col>
            <Col xs={12} sm={12} md={12} lg={7}>
              <div className="vpa-domain-setup-ml-bg-color">
                <Row className="p-10">
                  <Col xs={12}>
                    <Flex>
                      <Text className="page-text">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_domainsetup,
                          "content.verifyDomains.domainCopyTxt"
                        )}
                      </Text>
                    </Flex>
                  </Col>
                </Row>
                <Row className="p-5">
                  <Col xs={12}>
                    {this.props.domainDnsRecord.map((domain, index) => {
                      return (
                        <Row key={`domain-${index}`} className="m-b-10">
                          <Col xs={12} sm={12} md={6} lg={7}>
                            <Tooltip
                              content={
                                this.state.copieClipboard === true ? (
                                  <Text className="vpa-page-tooltip">
                                    Copied to clipboard
                                  </Text>
                                ) : (
                                  <Text className="vpa-page-tooltip">
                                    Copy to clipboard
                                  </Text>
                                )
                              }
                            >
                              <CopyToClipboard text={domain.domainName}>
                                <Button
                                  text
                                  primary
                                  iconOnly
                                  icon={<ClipboardCopiedToIcon />}
                                  onClick={() =>
                                    this.setState({ copieClipboard: true })
                                  }
                                />
                              </CopyToClipboard>
                            </Tooltip>
                            <Text className="page-text">
                              {domain.domainName}
                            </Text>
                          </Col>
                          <Col xs={12} sm={12} md={6} lg={4}>
                            <Flex>
                              <Input
                                type="text"
                                placeholder="TXT Record"
                                id={domain.domainName}
                                name={domain.domainName}
                                value={domain.textRecord}
                                inverted
                                onChange={(event) =>
                                  this.handleFormFieldChange(event, index)
                                }
                                className="page-textbox border m-r-10 border-b-primary-fade"
                              />
                              {this.getRecordstatus(domain)}
                            </Flex>
                          </Col>
                        </Row>
                      );
                    })}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="m-r-20 m-t-15 m-b-15">
              <Button
                primary
                content={IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.continue"
                )}
                disabled={!this.state.isFormDataFilled}
                onClick={this.handleRecordsSubmit}
                className="vpa-voice-setup-button"
              />
            </Col>
            <Col className="m-r-20 m-t-15 m-b-15">
              <Button
                primary
                content={IntlUtil.getText(
                  this._intl_ns_vpa_domainsetup,
                  "content.back"
                )}
                onClick={this.handleBackRegions}
                className="vpa-voice-setup-button"
              />
            </Col>
          </Row>
        </PageLoader>
        <PageExitGuard
          when={this.state.isError === false}
          navigate={(path) => this.props.history.push(path)}
          shouldBlockNavigation={(location) => {
            if (location.pathname) {
              return true;
            }

            return false;
          }}
        />
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  domainSetupProgressPage: state.domainSetupStore.domainSetupProgressPage,
  domainSetupProgressAccountNumber:
    state.domainSetupStore.domainSetupProgressAccountNumber,
  domainDnsRecord: state.domainSetupStore.domainDnsRecord,
});

const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
  setDomainSetupProgressPage,
  getDomainRecords,
  setUpdatedDomainRecords,
};

export default connect(mapStateToProps, mapActionToProps)(Domains);
